// frontend/src/pages/OverallDashboardPage.js
// Componente del Dashboard de Iniciativas, con filtros y métricas dinámicas.
// VERSIÓN FINAL v2: Gráficos y métricas contextuales.
// ACTUALIZADO: Rediseño de la sección de filtros para ser más clara y profesional.
// CORRECCIÓN: Ajustado el tamaño del campo "Número de Oportunidad" y mejorado el espaciado general.
// NUEVA FUNCIONALIDAD: Añadido selector para tipo de filtro de fecha (createdAt, startDate, closureDate).
// MEJORA DE DISEÑO UI: Rediseño completo del layout de "Filtros Avanzados" para mayor claridad y estética.

import React, { useEffect, useState, useMemo } from 'react';
import { useDashboardStore } from '../store/dashboardStore';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { useTemplateStore } from '../store/templateStore';
import ProjectList from '../components/ProjectList';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { CurrencyDollarIcon, PresentationChartLineIcon, BriefcaseIcon, FunnelIcon, PlusIcon, CalendarDaysIcon } from '@heroicons/react/24/solid';

const currencyFormatter = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const compactCurrencyFormatter = new Intl.NumberFormat('es-CL', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
});

const toNumber = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatCurrency = (value) => currencyFormatter.format(toNumber(value));

const formatCompactCurrency = (value) => `$${compactCurrencyFormatter.format(toNumber(value))}`;

// Componente para las tarjetas de KPI individuales
const KpiCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50 text-blue-800',
    green: 'border-green-500 bg-green-50 text-green-800',
    purple: 'border-purple-500 bg-purple-50 text-purple-800',
  };
  const iconColorClasses = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
  }

  return (
    <div className={`p-5 rounded-lg shadow-md border-l-4 ${colorClasses[color]}`}>
      <div className="flex items-center">
        <div className={`mr-4 p-3 bg-white rounded-full ${iconColorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  );
};

const MetricComparisonPanel = ({ title, expectedAmount, realizedAmount }) => {
  const expected = toNumber(expectedAmount);
  const realized = toNumber(realizedAmount);
  const remaining = Math.max(expected - realized, 0);
  const realizedPercent = expected > 0 ? Math.min(Math.round((realized / expected) * 100), 100) : 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Métricas</p>
          <h3 className="mt-1 text-xl font-bold text-slate-950">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">Comparación entre monto esperado y monto realizado.</p>
        </div>
        <div className="rounded-md bg-slate-50 px-4 py-3 text-right">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Avance financiero</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{realizedPercent}%</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-md border border-blue-100 bg-blue-50 p-4">
          <p className="text-sm font-medium text-blue-700">Monto Esperado</p>
          <p className="mt-2 truncate text-2xl font-bold text-blue-950" title={formatCurrency(expected)}>{formatCurrency(expected)}</p>
        </div>
        <div className="rounded-md border border-emerald-100 bg-emerald-50 p-4">
          <p className="text-sm font-medium text-emerald-700">Monto Realizado</p>
          <p className="mt-2 truncate text-2xl font-bold text-emerald-950" title={formatCurrency(realized)}>{formatCurrency(realized)}</p>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-medium text-slate-600">Brecha Pendiente</p>
          <p className="mt-2 truncate text-2xl font-bold text-slate-950" title={formatCurrency(remaining)}>{formatCurrency(remaining)}</p>
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700">Realizado sobre esperado</span>
          <span className="text-slate-500">{formatCurrency(realized)} / {formatCurrency(expected)}</span>
        </div>
        <div className="h-4 overflow-hidden rounded-full bg-slate-100 ring-1 ring-inset ring-slate-200">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{ width: `${realizedPercent}%` }}
            aria-label={`Monto realizado ${realizedPercent}%`}
          />
        </div>
      </div>
    </div>
  );
};

// Componente principal del Dashboard
function OverallDashboardPage({ onViewRevenueAnalysis, onAddProject, onEditProject, onViewProjectDetails }) {
  const { summaryData, isLoadingDashboard, fetchDashboardSummary } = useDashboardStore();
  const { projects, isLoadingProjects, fetchProjects } = useProjectStore();
  const { fetchClients, accountManagers, solutionSales } = useClientStore();
  const { initiativeTypes, fetchInitiativeTypes } = useTemplateStore();

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    opportunityNumber: '',
    accountManager: '',
    solutionSales: '',
    status: '',
    initiativeTypeId: '',
    projectName: '',
    dateFilterType: 'createdAt',
  });

  useEffect(() => {
    fetchClients();
    fetchInitiativeTypes();
  }, [fetchClients, fetchInitiativeTypes]);

  useEffect(() => {
    const handler = setTimeout(() => {
        fetchDashboardSummary(filters);
        fetchProjects(filters);
    }, 500);

    return () => {
        clearTimeout(handler);
    };
  }, [filters, fetchDashboardSummary, fetchProjects]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleInitiativeTypeFilter = (typeId) => {
    setFilters(prev => ({ ...prev, initiativeTypeId: typeId }));
  };

  const setDateRange = (period) => {
    const end = new Date();
    const start = new Date();

    switch (period) {
        case 'current_month': start.setDate(1); break;
        case 'last_month': start.setMonth(start.getMonth() - 1); start.setDate(1); end.setDate(0); break;
        case 'last_3_months': start.setMonth(start.getMonth() - 3); break;
        case 'last_6_months': start.setMonth(start.getMonth() - 6); break;
        case 'last_12_months': start.setMonth(start.getMonth() - 12); break;
        case 'all': // Reset dates
        default: setFilters(prev => ({ ...prev, startDate: '', endDate: '' })); return;
    }

    setFilters(prev => ({
        ...prev,
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0]
    }));
  };

  const selectedInitiativeName = useMemo(() => {
      if (!filters.initiativeTypeId) return 'Todos';
      return initiativeTypes.find(t => t.id === filters.initiativeTypeId)?.name || 'Todos';
  }, [filters.initiativeTypeId, initiativeTypes]);

  // Lógica de gráfico contextual
  const contextualChartData = useMemo(() => {
    if (selectedInitiativeName === 'MAP') {
        const phases = ['OLA', 'MRA', 'MPA'];
        return phases.map(phase => ({
            name: phase,
            Esperado: summaryData.phaseExpectedAmounts[phase] || 0,
            Realizado: summaryData.phaseRealizedAmounts[phase] || 0,
        }));
    }
    // Para "Todos" y cualquier otro tipo de iniciativa, mostrar los totales.
    return [{
        name: selectedInitiativeName === 'Todos' ? 'Totales' : selectedInitiativeName,
        Esperado: summaryData.totalExpectedAmount || 0,
        Realizado: summaryData.totalRealizedAmount || 0,
    }];
  }, [summaryData, selectedInitiativeName]);

  const isLoading = isLoadingDashboard || isLoadingProjects;

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-full">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard de Iniciativas</h1>
            <p className="mt-1 text-sm text-gray-600">Vista consolidada de las métricas y el estado de todas las iniciativas.</p>
          </div>
          <div className="flex items-center space-x-2 mt-4 md:mt-0">
            <button onClick={onAddProject} className="px-4 py-2 bg-green-600 text-white rounded-md shadow-sm hover:bg-green-700 flex items-center justify-center space-x-2 transition-colors"><PlusIcon className="h-5 w-5" /><span>Añadir Iniciativa</span></button>
            <button onClick={onViewRevenueAnalysis} className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 flex items-center justify-center space-x-2 transition-colors"><PresentationChartLineIcon className="h-5 w-5" /><span>Análisis de Ingresos</span></button>
          </div>
        </div>

        <div className="mb-6">
            <div className="flex items-center space-x-2 border-b border-gray-200">
                <button onClick={() => handleInitiativeTypeFilter('')} className={`px-4 py-2 text-sm font-medium rounded-t-md ${filters.initiativeTypeId === '' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>Todos</button>
                {initiativeTypes.map(type => (
                    <button key={type.id} onClick={() => handleInitiativeTypeFilter(type.id)} className={`px-4 py-2 text-sm font-medium rounded-t-md ${filters.initiativeTypeId === type.id ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>{type.name}</button>
                ))}
            </div>
        </div>

        {/* Sección de filtros rediseñada - Inicio */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6 border border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800 mb-5 flex items-center"><FunnelIcon className="h-6 w-6 mr-2" />Filtros Avanzados</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6"> {/* Ajustado para 4 columnas en lg y más espacio */}

            {/* Fila 1: Filtros de Selección y Texto Corto */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select name="status" value={filters.status} onChange={handleFilterChange} className="w-full border-gray-300 rounded-md shadow-sm text-sm p-2.5">
                <option value="">Todos los Estados</option>
                <option value="active">Activo</option>
                <option value="closed">Cerrado</option>
                <option value="pending">Pendiente</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
            <div>
              <label htmlFor="accountManager" className="block text-sm font-medium text-gray-700 mb-1">Account Manager</label>
              <select name="accountManager" value={filters.accountManager} onChange={handleFilterChange} className="w-full border-gray-300 rounded-md shadow-sm text-sm p-2.5">
                <option value="">Todos los Account Managers</option>
                {accountManagers.map(am => <option key={am} value={am}>{am}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="solutionSales" className="block text-sm font-medium text-gray-700 mb-1">Solution Sales</label>
              <select name="solutionSales" value={filters.solutionSales} onChange={handleFilterChange} className="w-full border-gray-300 rounded-md shadow-sm text-sm p-2.5">
                <option value="">Todos los Solution Sales</option>
                {solutionSales.map(ss => <option key={ss} value={ss}>{ss}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-1">Nombre de Proyecto</label>
              <input type="text" name="projectName" value={filters.projectName} onChange={handleFilterChange} placeholder="Buscar por Nombre..." className="w-full border-gray-300 rounded-md shadow-sm text-sm p-2.5" />
            </div>

            {/* Fila 2: Número de Oportunidad y Rango de Fechas */}
            <div>
              <label htmlFor="opportunityNumber" className="block text-sm font-medium text-gray-700 mb-1">Número de Oportunidad</label>
              <input type="text" name="opportunityNumber" value={filters.opportunityNumber} onChange={handleFilterChange} placeholder="Nº Oportunidad..." className="w-full border-gray-300 rounded-md shadow-sm text-sm p-2.5" />
            </div>

            {/* Contenedor del Rango de Fechas - Mayor espacio y mejor organizado */}
            <div className="lg:col-span-3"> {/* Ocupa 3 columnas en pantallas grandes para mayor espacio */}
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><CalendarDaysIcon className="h-4 w-4 mr-1 text-gray-500"/> Rango de Fechas</label>

              {/* Selector de tipo de fecha */}
              <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3"> {/* Ajustado el gap para los radios */}
                <div className="flex items-center">
                  <input id="filter-created-at" name="dateFilterType" type="radio" value="createdAt" checked={filters.dateFilterType === 'createdAt'} onChange={handleFilterChange} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" />
                  <label htmlFor="filter-created-at" className="ml-2 block text-sm text-gray-900">Fecha de Creación</label>
                </div>
                <div className="flex items-center">
                  <input id="filter-start-date" name="dateFilterType" type="radio" value="startDate" checked={filters.dateFilterType === 'startDate'} onChange={handleFilterChange} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" />
                  <label htmlFor="filter-start-date" className="ml-2 block text-sm text-gray-900">Fecha Inicio</label>
                </div>
                <div className="flex items-center">
                  <input id="filter-closure-date" name="dateFilterType" type="radio" value="closureDate" checked={filters.dateFilterType === 'closureDate'} onChange={handleFilterChange} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" />
                  <label htmlFor="filter-closure-date" className="ml-2 block text-sm text-gray-900">Fecha Cierre Estimado</label>
                </div>
              </div>

              {/* Inputs de fecha y botones rápidos */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"> {/* Mantiene los inputs de fecha en 2 columnas */}
                <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="w-full border-gray-300 rounded-md shadow-sm text-sm p-2.5" />
                <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="w-full border-gray-300 rounded-md shadow-sm text-sm p-2.5" />
              </div>
              <div className="flex items-center space-x-2 mt-3 flex-wrap gap-y-2"> {/* Botones de rango de fechas */}
                  <button onClick={() => setDateRange('current_month')} className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors">Este Mes</button>
                  <button onClick={() => setDateRange('last_3_months')} className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors">3 Meses</button>
                  <button onClick={() => setDateRange('last_6_months')} className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors">6 Meses</button>
                  <button onClick={() => setDateRange('last_12_months')} className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors">12 Meses</button>
                  <button onClick={() => setDateRange('all')} className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors">Todos</button>
              </div>
            </div>
          </div>
        </div>
        {/* Sección de filtros rediseñada - Fin */}


        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KpiCard title="Monto Total Esperado" value={formatCurrency(summaryData.totalExpectedAmount)} icon={<CurrencyDollarIcon className="h-6 w-6"/>} color="blue" />
              <KpiCard title="Monto Total Realizado" value={formatCurrency(summaryData.totalRealizedAmount)} icon={<CurrencyDollarIcon className="h-6 w-6"/>} color="green" />
              <KpiCard title="Iniciativas Activas" value={summaryData.activeProjectsCount} icon={<BriefcaseIcon className="h-6 w-6"/>} color="purple" />
            </div>

            {contextualChartData.length === 1 ? (
              <MetricComparisonPanel
                title={`Métricas de ${selectedInitiativeName}`}
                expectedAmount={contextualChartData[0].Esperado}
                realizedAmount={contextualChartData[0].Realizado}
              />
            ) : (
              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Métricas de {selectedInitiativeName}</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={contextualChartData} margin={{ top: 10, right: 24, left: 24, bottom: 8 }} barCategoryGap="28%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tickLine={false} axisLine={false} />
                    <YAxis width={76} tickFormatter={formatCompactCurrency} tickLine={false} axisLine={false} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend verticalAlign="bottom" height={36} />
                    <Bar dataKey="Esperado" fill="#3b82f6" name="Monto Esperado" radius={[6, 6, 0, 0]} maxBarSize={96} />
                    <Bar dataKey="Realizado" fill="#22c55e" name="Monto Realizado" radius={[6, 6, 0, 0]} maxBarSize={96} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Lista de Iniciativas</h3>
                <ProjectList projects={projects} isLoading={isLoading} onEditProject={onEditProject} onViewProjectDetails={onViewProjectDetails} activeFilters={filters}/>
            </div>
        </div>
      </div>
    </div>
  );
}

export default OverallDashboardPage;